"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useWaveDropsSearch } from "@/hooks/useWaveDropsSearch";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FocusTrap } from "focus-trap-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import Drop, { DropLocation } from "../Drop";
const MIN_QUERY_LENGTH = 2;
const normalize = (value: string) => value.trim();
export default function WaveDropsSearchModal(t0) {
  const $ = _c(81);
  const {
    isOpen,
    onClose,
    wave,
    onSelectSerialNo
  } = t0;
  const modalRef = useRef(null);
  let t1;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t1 = () => {
      if (isOpen) {
        onClose();
      }
    };
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useClickAway(modalRef, t1);
  let t2;
  if ($[3] !== isOpen || $[4] !== onClose) {
    t2 = () => {
      if (isOpen) {
        onClose();
      }
    };
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useKeyPressEvent("Escape", t2);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  let t3;
  if ($[6] !== query) {
    t3 = () => setDebouncedQuery(query);
    $[6] = query;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== query) {
    t4 = [query];
    $[8] = query;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useDebounce(t3, 250, t4);
  let t5;
  let t6;
  if ($[10] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        setQuery("");
        setDebouncedQuery("");
      }
    };
    t6 = [isOpen];
    $[10] = isOpen;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== debouncedQuery) {
    t7 = normalize(debouncedQuery);
    $[13] = debouncedQuery;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const normalizedQuery = t7;
  const meetsMinLength = normalizedQuery.length >= MIN_QUERY_LENGTH;
  const t8 = isOpen && meetsMinLength;
  let t9;
  if ($[15] !== normalizedQuery || $[16] !== t8 || $[17] !== wave) {
    t9 = {
      wave,
      term: normalizedQuery,
      enabled: t8,
      size: 50
    };
    $[15] = normalizedQuery;
    $[16] = t8;
    $[17] = wave;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const {
    drops: results,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useWaveDropsSearch(t9);
  const inputRef = useRef(null);
  let t10;
  let t11;
  if ($[19] !== isOpen) {
    t10 = () => {
      if (!isOpen) {
        return;
      }
      const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    };
    t11 = [isOpen];
    $[19] = isOpen;
    $[20] = t10;
    $[21] = t11;
  } else {
    t10 = $[20];
    t11 = $[21];
  }
  useEffect(t10, t11);
  if (!isOpen || typeof document === "undefined") {
    return null;
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      allowOutsideClick: true,
      fallbackFocus: () => modalRef.current as HTMLElement | null ?? inputRef.current as HTMLElement | null ?? document.body,
      initialFocus: () => inputRef.current as HTMLElement | null ?? modalRef.current as HTMLElement | null ?? document.body
    };
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50 tw-backdrop-blur-[1px]" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronLeftIcon className="tw-size-6 tw-flex-shrink-0" />;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== onClose) {
    t15 = <button type="button" onClick={onClose} aria-label="Close search" className="tw-flex sm:tw-hidden tw-size-7 tw-bg-transparent -tw-ml-1 tw-mr-1 tw-border-none tw-rounded-full tw-items-center tw-justify-center tw-text-iron-300 hover:tw-text-white tw-transition tw-duration-150">{t14}</button>;
    $[25] = onClose;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="tw-text-xs tw-text-iron-400 tw-tracking-wide">Search in</div>;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== wave.name) {
    t17 = <div className="tw-min-w-0 tw-flex-1">{t16}<div className="tw-text-sm tw-font-semibold tw-text-iron-50 tw-truncate">{wave.name}</div></div>;
    $[28] = wave.name;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <XMarkIcon className="tw-size-5 tw-flex-shrink-0" />;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== onClose) {
    t19 = <button type="button" onClick={onClose} aria-label="Close search" className="tw-hidden sm:tw-inline-flex tw-items-center tw-justify-center tw-size-9 tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white tw-transition tw-duration-150">{t18}</button>;
    $[31] = onClose;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t15 || $[34] !== t17 || $[35] !== t19) {
    t20 = <div className="tw-border-b tw-border-x-0 tw-border-t-0 tw-border-solid tw-border-white/10 tw-px-4 tw-py-4 tw-flex tw-items-center tw-gap-2">{t15}{t17}{t19}</div>;
    $[33] = t15;
    $[34] = t17;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  let t22;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <svg className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-3.5 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>;
    t22 = <label className="tw-sr-only" htmlFor="wave-drops-search-input">Search messages</label>;
    $[37] = t21;
    $[38] = t22;
  } else {
    t21 = $[37];
    t22 = $[38];
  }
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = e => setQuery(e.target.value);
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== query) {
    t24 = <input id="wave-drops-search-input" ref={inputRef} type="text" autoComplete="off" value={query} onChange={t23} className="tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-py-3 tw-pl-11 tw-pr-10 tw-bg-iron-900 tw-text-iron-50 tw-font-normal tw-caret-primary-300 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 hover:tw-ring-iron-600 placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-bg-transparent focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-300 tw-text-base sm:text-sm tw-transition tw-duration-300 tw-ease-out" placeholder="Search messages" />;
    $[40] = query;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== query.length) {
    t25 = query.length > 0 && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-flex tw-items-center tw-justify-center tw-size-8 tw-rounded-full tw-bg-transparent tw-border-0 tw-text-iron-400 hover:tw-text-iron-100 tw-transition tw-duration-150"><XMarkIcon className="tw-size-5 tw-flex-shrink-0" /></button>;
    $[42] = query.length;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== t24 || $[45] !== t25) {
    t26 = <div className="tw-px-4 tw-pb-4"><div className="tw-relative">{t21}{t22}{t24}{t25}</div></div>;
    $[44] = t24;
    $[45] = t25;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== isLoading) {
    t27 = isLoading && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-300">Loading…</div>;
    $[47] = isLoading;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== isError || $[50] !== isLoading) {
    t28 = !isLoading && isError && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-300">Couldn’t load drops for search.</div>;
    $[49] = isError;
    $[50] = isLoading;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== isError || $[53] !== isLoading || $[54] !== meetsMinLength) {
    t29 = !isLoading && !isError && !meetsMinLength && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-400 tw-text-sm">Type at least {MIN_QUERY_LENGTH} characters to search.</div>;
    $[52] = isError;
    $[53] = isLoading;
    $[54] = meetsMinLength;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== isError || $[57] !== isLoading || $[58] !== meetsMinLength || $[59] !== results) {
    t30 = !isLoading && !isError && meetsMinLength && results.length === 0 && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-400 tw-text-sm">No matches found.</div>;
    $[56] = isError;
    $[57] = isLoading;
    $[58] = meetsMinLength;
    $[59] = results;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== fetchNextPage || $[62] !== hasNextPage || $[63] !== isError || $[64] !== isFetchingNextPage || $[65] !== isLoading || $[66] !== meetsMinLength || $[67] !== onClose || $[68] !== onSelectSerialNo || $[69] !== results) {
    t31 = !isLoading && !isError && meetsMinLength && results.length > 0 && <div className="tw-space-y-2"><div className="tw-text-xs tw-text-iron-400">{results.length} result{results.length === 1 ? "" : "s"}</div><div className="tw-space-y-2">{results.map((drop, index) => {
          const previousDrop = results[index - 1] ?? null;
          const nextDrop = results[index + 1] ?? null;
          const serialNo = drop.serial_no;
          const canSelect = typeof serialNo === "number";
          return <button type="button" key={drop.stableKey} disabled={!canSelect} onClick={() => {
            if (!canSelect) {
              return;
            }
            onSelectSerialNo(serialNo);
            onClose();
          }} className="tw-w-full tw-text-left tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 desktop-hover:hover:tw-border-iron-600 desktop-hover:hover:tw-bg-iron-900/40 tw-transition tw-duration-150 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"><div className="tw-pointer-events-none"><Drop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={_temp} onQuote={_temp2} onReplyClick={_temp3} onQuoteClick={_temp4} /></div></button>;
        })}</div>{hasNextPage && <div className="tw-flex tw-justify-center tw-pt-2"><button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-iron-200 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition tw-duration-150">{isFetchingNextPage ? "Loading\u2026" : "Load more"}</button></div>}</div>;
    $[61] = fetchNextPage;
    $[62] = hasNextPage;
    $[63] = isError;
    $[64] = isFetchingNextPage;
    $[65] = isLoading;
    $[66] = meetsMinLength;
    $[67] = onClose;
    $[68] = onSelectSerialNo;
    $[69] = results;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t27 || $[72] !== t28 || $[73] !== t29 || $[74] !== t30 || $[75] !== t31) {
    t32 = <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-4 tw-pb-6">{t27}{t28}{t29}{t30}{t31}</div>;
    $[71] = t27;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t20 || $[78] !== t26 || $[79] !== t32) {
    t33 = createPortal(<FocusTrap focusTrapOptions={t12}><div className="tailwind-scope tw-cursor-default tw-relative tw-z-1000">{t13}<div className="tw-fixed tw-inset-0 tw-z-1000 tw-overflow-y-auto"><div className="tw-flex tw-min-h-full tw-items-start tw-justify-center tw-p-0 tw-text-center sm:tw-items-center sm:tw-p-6"><div ref={modalRef} aria-modal="true" aria-labelledby="wave-drops-search-input" className="tw-w-full tw-h-full sm:tw-h-[70vh] tw-max-w-[min(100vw,900px)] tw-transform sm:tw-rounded-xl tw-bg-iron-950 tw-text-left tw-shadow-xl tw-transition-all tw-overflow-hidden inset-safe-area tw-flex tw-flex-col tw-min-h-0 tw-border tw-border-solid tw-border-iron-800">{t20}{t26}{t32}</div></div></div></div></FocusTrap>, document.body);
    $[77] = t20;
    $[78] = t26;
    $[79] = t32;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  return t33;
}
function _temp4() {}
function _temp3() {}
function _temp2() {}
function _temp() {}